import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RevealHeading, Reveal } from "@/components/Reveal";
import { DrawReveal } from "@/components/DrawReveal";
import { Gauge, ArrowUpRight } from "lucide-react";

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/vehicles")
      .then((res) => setVehicles(res.data))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-void min-h-screen" data-testid="vehicles-page">
      <Navbar />

      <section className="pt-32 md:pt-44 pb-16 md:pb-24 border-b border-[#222]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-neon mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-neon" /> Platforms We Support
          </div>
          <h1 className="font-display uppercase text-6xl md:text-8xl lg:text-[10vw] leading-[0.82] tracking-tight">
            <RevealHeading lines={["Supported", "Vehicles"]} />
          </h1>
          <p className="mt-8 font-mono text-sm md:text-base text-[#9CA3AF] max-w-xl">
            The platforms we know inside out. Don&apos;t see yours? We support far more — send a request and we&apos;ll confirm.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 md:px-10 py-16 md:py-24">
        {loading ? (
          <div className="font-mono text-[#9CA3AF] uppercase tracking-[0.2em] text-sm">Loading platforms…</div>
        ) : vehicles.length === 0 ? (
          <div className="font-mono text-[#9CA3AF]">No vehicles listed yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#222]">
            {vehicles.map((v, i) => (
              <Reveal key={v.id} delay={(i % 3) * 0.06}>
                <div
                  data-testid={`vehicle-card-${v.id}`}
                  className="group relative border-r border-b border-[#222] bg-matte hover:bg-panel transition-colors"
                >
                  <div className="relative overflow-hidden aspect-[16/10] border-b border-[#222]">
                    {v.image_url ? (
                      <DrawReveal
                        src={v.image_url}
                        alt={`${v.make} ${v.model}`}
                        cover="#111111"
                        imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#333]">
                        <Gauge size={48} />
                      </div>
                    )}
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-1">{v.make}</div>
                        <h3 className="font-display uppercase text-3xl md:text-4xl leading-none tracking-tight text-[#FAFAFA]">
                          {v.model}
                        </h3>
                      </div>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2 text-xs font-mono">
                      {v.year_range && <span className="border border-[#333] px-2 py-1 text-[#9CA3AF]">{v.year_range}</span>}
                      {v.platform && <span className="border border-[#333] px-2 py-1 text-[#9CA3AF]">{v.platform}</span>}
                    </div>
                    {v.notes && <p className="mt-5 font-mono text-sm text-[#9CA3AF] leading-relaxed">{v.notes}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal>
          <div className="mt-16 border border-[#222] p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight">Don&apos;t see your car?</h3>
              <p className="mt-2 font-mono text-sm text-[#9CA3AF]">We work with far more than what&apos;s listed. Ask us.</p>
            </div>
            <Link
              to="/tuning"
              data-testid="vehicles-cta"
              className="group bg-neon text-void font-bold uppercase tracking-[0.15em] text-xs py-4 px-8 flex items-center gap-2 hover:bg-[#FAFAFA] transition-colors w-fit"
            >
              Request A Build <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
